import { useMutation } from '@apollo/client';
import { useMsal } from '@azure/msal-react';
import { PaperAirplaneIcon, UserCircleIcon } from '@heroicons/react/20/solid';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { omit } from 'lodash';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import Button from '../../../../components/buttons/Button';
import TextField from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toast/ToastProvider';
import { CREATE_POSITION, UPDATE_POSITION } from '../../../../graphql/positions/mutations';
import { GET_POSITIONS } from '../../../../graphql/positions/queries';

function Form({ onSuccess, data }) {
  const { _id } = useParams();
  const { accounts } = useMsal();
  const { toast } = useToast();
  const [createPosition] = useMutation(CREATE_POSITION, {
    refetchQueries: [{ query: GET_POSITIONS }, 'GetPositions']
  });
  const [updatePosition] = useMutation(UPDATE_POSITION, {
    refetchQueries: [{ query: GET_POSITIONS }, 'GetPositions']
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      createdBy: accounts[0]?.username,
      updatedBy: accounts[0]?.username
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Please enter the position name.')
    }),
    onSubmit: async (values, actions) => {
      if (_id) {
        const variables = omit(
          {
            ...values,
            _id,
            updatedAt: new Date()
          },
          'createdBy'
        );
        try {
          const result = await updatePosition({ variables });
          if (result.data.updatePosition) {
            if (onSuccess) {
              await onSuccess();
            }
            toast({
              title: 'Position Updated',
              description: `The department has been successfully updated at ${dayjs().format(
                'LLL'
              )}.`
            });
          }
        } catch (err) {
          toast({
            title: 'Error',
            description: err.message,
            type: 'error'
          });
        }
      } else {
        try {
          const result = await createPosition({ variables: values });
          if (result.data.createPosition) {
            toast({
              title: 'Position created',
              description: 'The position has been successfully created.'
            });
            if (onSuccess) {
              onSuccess();
            }
            actions.resetForm();
          }
        } catch (err) {
          toast({
            title: 'Error',
            description: err?.message,
            type: 'error'
          });
        }
      }
      actions.setSubmitting(false);
    }
  });

  useEffect(() => {
    if (data) {
      formik.setFieldValue('name', data.name);
    }
  }, [data]);

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      className="space-y-4 bg-orange-50 rounded-md p-4"
    >
      <TextField
        name="name"
        label="Position"
        required
        placeholder="Developer.."
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        value={formik.values.name}
        Icon={UserCircleIcon}
        autoComplete="off"
        error={formik.touched.name && formik.errors.name ? formik.errors.name : null}
      />
      <Button
        type="submit"
        Icon={PaperAirplaneIcon}
        label="Submit"
        loading={formik.isSubmitting}
        disabled={formik.isSubmitting}
      />
    </form>
  );
}

export default Form;
